import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { getAmenities, getMyBookings, bookAmenity, updateBookingStatus } from '@/services/api';
import { 
  Calendar, 
  Clock, 
  IndianRupee, 
  MapPin, 
  Users,
  CheckCircle2,
  XCircle,
  Dumbbell,
  Waves,
  PartyPopper,
  Building,
  Info,
  Ticket,
  Copy
} from 'lucide-react';

const TenantAmenities = () => {
  const { user } = useAuth();
  const [amenities, setAmenities] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({ date: '', time_slot: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [amenitiesData, bookingsData] = await Promise.all([
        getAmenities(),
        getMyBookings()
      ]);
      setAmenities(amenitiesData);
      setMyBookings(bookingsData);
    } catch (error) {
      toast.error('Failed to load amenities');
    } finally {
      setLoading(false);
    }
  };

  const handleBookAmenity = async () => {
    if (!bookingForm.date || !bookingForm.time_slot) {
      toast.error('Please select date and time slot');
      return;
    }

    try {
      setSubmitting(true);
      await bookAmenity({
        amenity_id: selectedAmenity.id,
        amenity_name: selectedAmenity.name,
        date: bookingForm.date,
        time_slot: bookingForm.time_slot
      });
      toast.success('Amenity booked successfully! A bill has been generated.');
      setBookingDialogOpen(false);
      setBookingForm({ date: '', time_slot: '' });
      setSelectedAmenity(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book amenity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await updateBookingStatus(bookingId, 'cancelled');
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const getAmenityIcon = (name) => {
    const lowerName = name?.toLowerCase() || '';
    if (lowerName.includes('gym') || lowerName.includes('fitness')) return <Dumbbell className="h-6 w-6" />;
    if (lowerName.includes('pool') || lowerName.includes('swim')) return <Waves className="h-6 w-6" />;
    if (lowerName.includes('club') || lowerName.includes('hall') || lowerName.includes('party')) return <PartyPopper className="h-6 w-6" />;
    return <Building className="h-6 w-6" />;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-700">Confirmed</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
      case 'used':
        return <Badge className="bg-green-100 text-green-700">Checked In</Badge>;
      case 'checked_out':
        return <Badge className="bg-teal-100 text-teal-700">Checked Out</Badge>;
      case 'expired':
        return <Badge className="bg-slate-100 text-slate-500">Expired</Badge>;
      case 'no_show':
        return <Badge className="bg-orange-100 text-orange-700">No Show</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const timeSlots = [
    '06:00 AM - 08:00 AM',
    '08:00 AM - 10:00 AM',
    '10:00 AM - 12:00 PM',
    '12:00 PM - 02:00 PM',
    '02:00 PM - 04:00 PM',
    '04:00 PM - 06:00 PM',
    '06:00 PM - 08:00 PM',
    '08:00 PM - 10:00 PM'
  ];

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const upcomingBookings = myBookings.filter(b => b.status === 'confirmed' || b.status === 'pending');
  const pastBookings = myBookings.filter(b => b.status === 'used' || b.status === 'checked_out' || b.status === 'cancelled' || b.status === 'no_show' || b.status === 'expired');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-amenities">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Amenities</h1>
        <p className="text-slate-600 mt-1">Book society amenities and facilities</p>
      </div>

      {/* Info Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-5 w-5 text-blue-600" />
        <AlertDescription className="text-blue-700">
          Booking an amenity will generate a bill that you can view in the Bills section. 
          Payment can be made through the Bills page.
        </AlertDescription>
      </Alert>

      {/* Available Amenities */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Available Amenities</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {amenities.map((amenity) => (
            <Card key={amenity.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-secondary/10 rounded-xl flex items-center justify-center text-secondary">
                    {getAmenityIcon(amenity.name)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{amenity.name}</CardTitle>
                    <CardDescription>{amenity.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-4 w-4" />
                    <span>{amenity.location || 'Society Premises'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="h-4 w-4" />
                    <span>Capacity: {amenity.capacity || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-1 text-lg font-bold text-secondary">
                    <IndianRupee className="h-5 w-5" />
                    {amenity.price?.toLocaleString('en-IN') || 0}
                    <span className="text-sm font-normal text-slate-500">/slot</span>
                  </div>
                  <Button 
                    onClick={() => {
                      setSelectedAmenity(amenity);
                      setBookingDialogOpen(true);
                    }}
                    data-testid={`book-amenity-${amenity.id}`}
                  >
                    Book Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* My Bookings */}
      {upcomingBookings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">My Upcoming Bookings</h2>
          <div className="space-y-3">
            {upcomingBookings.map((booking) => (
              <Card key={booking.id} className="border-green-200 bg-green-50/50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                        {getAmenityIcon(booking.amenity_name)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-900">{booking.amenity_name}</p>
                          {booking.booking_code && (
                            <Badge 
                              variant="outline" 
                              className="font-mono bg-secondary/10 text-secondary border-secondary/30 cursor-pointer hover:bg-secondary/20"
                              onClick={() => {
                                try {
                                  if (navigator.clipboard?.writeText) {
                                    navigator.clipboard.writeText(booking.booking_code).then(() => toast.success('Booking code copied!')).catch(() => toast.info('Code: ' + booking.booking_code));
                                  } else {
                                    const ta = document.createElement('textarea'); ta.value = booking.booking_code; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                                    toast.success('Booking code copied!');
                                  }
                                } catch { toast.info('Code: ' + booking.booking_code); }
                              }}
                              data-testid="booking-code-badge"
                            >
                              <Ticket className="h-3 w-3 mr-1" />
                              {booking.booking_code}
                              <Copy className="h-3 w-3 ml-1" />
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {booking.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {booking.time_slot}
                          </span>
                        </div>
                        {booking.booking_code && booking.status === 'confirmed' && (
                          <p className="text-xs text-slate-400 mt-1">Show this code to security for check-in</p>
                        )}
                        {booking.payment_status === 'pending' && booking.status === 'confirmed' && (
                          <p className="text-xs text-red-600 font-medium mt-1">Payment pending — pay the amenity bill before check-in</p>
                        )}
                        {booking.payment_status === 'paid' && booking.status === 'confirmed' && (
                          <p className="text-xs text-green-600 font-medium mt-1">Payment complete — ready for check-in</p>
                        )}
                        {booking.checked_in_at && (
                          <p className="text-xs text-green-600 mt-1">Checked in at {new Date(booking.checked_in_at).toLocaleTimeString()} by {booking.checked_in_by}</p>
                        )}
                        {booking.checked_out_at && (
                          <p className="text-xs text-teal-600 mt-1">Checked out at {new Date(booking.checked_out_at).toLocaleTimeString()} by {booking.checked_out_by}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(booking.status)}
                      {booking.status !== 'used' && booking.status !== 'checked_out' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleCancelBooking(booking.id)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Past Bookings */}
      {pastBookings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Past Bookings</h2>
          <div className="space-y-2">
            {pastBookings.slice(0, 5).map((booking) => (
              <Card key={booking.id} className="bg-slate-50">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500">
                        {getAmenityIcon(booking.amenity_name)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">{booking.amenity_name}</p>
                        <p className="text-xs text-slate-500">{booking.date} • {booking.time_slot}</p>
                      </div>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book {selectedAmenity?.name}</DialogTitle>
            <DialogDescription>
              Select your preferred date and time slot
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <input
                type="date"
                min={getTodayDate()}
                value={bookingForm.date}
                onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="booking-date"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Time Slot</label>
              <Select value={bookingForm.time_slot} onValueChange={(v) => setBookingForm({ ...bookingForm, time_slot: v })}>
                <SelectTrigger data-testid="booking-time-slot">
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Booking Fee</span>
                <span className="font-bold text-secondary flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" />
                  {selectedAmenity?.price?.toLocaleString('en-IN') || 0}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">A bill will be generated for this booking</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBookAmenity} disabled={submitting} data-testid="confirm-booking-btn">
              {submitting ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantAmenities;
